import {
  Icon,
  IconButton,
  SelectionList,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import {
  AnalyticsMenu,
  AnalyticsMenuAction,
  AnalyticsMenuActions,
} from 'lib-analytics-ui';
import { DashboardFavorite } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';

import { useDashboardLandingPage } from 'app/views/layout/views/dashboard/utils/useDashboardLandingPage';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import styles from './queueDashboardFilterControlFavoritesItem.module.scss';

type QueueDashboardFilterControlFavoritesItemProps = {
  favorite: DashboardFavorite;
  onEdit: (favorite: DashboardFavorite) => void;
  onDelete: (favorite: DashboardFavorite) => void;
  onToggleLandingPage: (favorite: DashboardFavorite) => void;
  onTransferOwnership: (favorite: DashboardFavorite) => void;
};

export const QueueDashboardFilterControlFavoritesItem = memo(
  function QueueDashboardFilterControlFavoritesItem({
    favorite,
    onEdit,
    onDelete,
    onToggleLandingPage,
    onTransferOwnership,
  }: QueueDashboardFilterControlFavoritesItemProps): JSX.Element {
    const { user } = useCurrentUser();
    const { isLandingPage } = useDashboardLandingPage();
    const hasAnalyticsAccess = useHasAccess('analytics');

    const [isHovered, setIsHovered] = useState<boolean>(false);

    const handleEdit = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onEdit(favorite);
      },
      [favorite, onEdit],
    );

    const handleDelete = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDelete(favorite);
      },
      [favorite, onDelete],
    );

    const handleToggleLandingPage = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onToggleLandingPage(favorite);
      },
      [favorite, onToggleLandingPage],
    );

    const handleTransferOwnership = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onTransferOwnership(favorite);
      },
      [favorite, onTransferOwnership],
    );

    /** Only the owner of the favorite can modify its details and visibility. */
    const isOwner = favorite?.user.id === user.id;

    /**
     * Only the owner and users with full access to Analytics can transfer
     * ownership of favorites that are shared.
     */
    const canTransferOwnership =
      (isOwner || hasAnalyticsAccess) && !!favorite.shared;

    const actions = useMemo((): AnalyticsMenuAction[] => {
      const isCurrentLandingPage = isLandingPage(favorite);

      return [
        ...(isOwner
          ? ([
              {
                type: 'button',
                icon: 'pen',
                label: 'Edit',
                onClick: handleEdit,
              },
            ] as AnalyticsMenuActions)
          : []),
        {
          type: 'button',
          icon: isCurrentLandingPage ? 'link-slash' : 'link',
          label: isCurrentLandingPage
            ? 'Remove as my landing page'
            : 'Set as my landing page',
          tooltip: isCurrentLandingPage
            ? 'This favorite will no longer be shown by default when you log in.'
            : 'This favorite will be shown by default the first time you log in.',
          onClick: handleToggleLandingPage,
        },
        ...(canTransferOwnership
          ? ([
              {
                type: 'divider',
              },
              {
                type: 'button',
                icon: 'exchange',
                label: 'Transfer ownership',
                onClick: handleTransferOwnership,
              },
            ] as AnalyticsMenuActions)
          : []),
        ...(isOwner
          ? ([
              {
                type: 'divider',
              },
              {
                type: 'button',
                icon: 'trash',
                label: 'Delete',
                onClick: handleDelete,
              },
            ] as AnalyticsMenuActions)
          : []),
      ];
    }, [
      canTransferOwnership,
      favorite,
      handleDelete,
      handleEdit,
      handleToggleLandingPage,
      handleTransferOwnership,
      isLandingPage,
      isOwner,
    ]);

    return (
      <SelectionList.Item
        key={favorite.id}
        value={favorite.id}
        className={classNames({
          [styles.item]: true,
          [styles.itemHovered]: isHovered,
        })}
        contentClassName={styles.itemContent}
        noFixedSelectionStateSpace
        onFocus={() => setIsHovered(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div>
          {favorite.name}

          {favorite.description && (
            <Tooltip
              className={styles.tooltip}
              content={favorite.description}
              wrap="span"
              wrapProps={{ className: styles.descriptionIcon }}
            >
              <Icon name="info" color="tertiary" />
            </Tooltip>
          )}
        </div>

        <div className={styles.itemActions}>
          <AnalyticsMenu
            actions={actions}
            buttonProps={{
              className: styles.actionsButton,
            }}
          />

          {!isOwner && favorite.shared && (
            <Tooltip
              content={`Shared with you by ${getUserDisplayName(favorite.user)}`}
            >
              <IconButton icon="users" variant="tertiary" disabled />
            </Tooltip>
          )}
        </div>
      </SelectionList.Item>
    );
  },
);
